import Alpine from 'alpinejs';
import widgetCss from './styles.css?inline';
import { registerChatWidget, chatWidgetTemplate } from './chat.js';
import { createApiClient } from './api.js';

function resolveScript(fallbackScript = null, options = {}) {
    if (options.botKey) {
        return {
            getAttribute(name) {
                if (name === 'data-bot-key') {
                    return options.botKey;
                }

                if (name === 'data-api-base') {
                    return options.apiBase ?? null;
                }

                return null;
            },
            src: options.scriptUrl ?? '',
        };
    }

    return document.currentScript ?? fallbackScript;
}

function resolveApiBase(script) {
    const fromData = script?.getAttribute('data-api-base');

    if (fromData) {
        return fromData.replace(/\/$/, '');
    }

    if (script?.src) {
        try {
            const url = new URL(script.src);

            return `${url.origin}/api`;
        } catch {
            // fall through
        }
    }

    return `${window.location.origin}/api`;
}

function mountShadowHost(botKey) {
    const host = document.createElement('div');
    host.id = `chatbot-widget-host-${botKey}`;
    document.body.appendChild(host);

    return host.attachShadow({ mode: 'closed' });
}

function isHostAlpineReady(candidate) {
    return candidate
        && typeof candidate.data === 'function'
        && typeof candidate.initTree === 'function';
}

/**
 * Csak a saját Filament admin panel — NEM minden Livewire/Laravel oldal.
 * Ügyfél webshop / legacy: saját Alpine.start(), „beillesztés és működik”.
 */
function shouldDeferToHostAlpine() {
    if (/\/admin(\/|$)/.test(window.location.pathname)) {
        return true;
    }

    return document.querySelector(
        'script[src*="filament"], link[href*="filament"]',
    ) !== null;
}

function waitForDocumentReady() {
    if (document.readyState === 'complete') {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        window.addEventListener('load', resolve, { once: true });
    });
}

/**
 * @returns {Promise<import('alpinejs').Alpine | null>}
 */
function waitForHostAlpine(timeoutMs = 30000) {
    if (isHostAlpineReady(window.Alpine)) {
        return Promise.resolve(window.Alpine);
    }

    if (! shouldDeferToHostAlpine()) {
        return Promise.resolve(null);
    }

    return new Promise((resolve) => {
        const deadline = Date.now() + timeoutMs;

        const finish = (host) => {
            document.removeEventListener('alpine:init', onAlpineInit);
            document.removeEventListener('livewire:init', onLivewireInit);
            resolve(host);
        };

        const tryResolve = () => {
            if (isHostAlpineReady(window.Alpine)) {
                finish(window.Alpine);

                return true;
            }

            return false;
        };

        const onAlpineInit = () => tryResolve();
        const onLivewireInit = () => requestAnimationFrame(() => tryResolve());

        document.addEventListener('alpine:init', onAlpineInit);
        document.addEventListener('livewire:init', onLivewireInit);

        const tick = () => {
            if (tryResolve()) {
                return;
            }

            if (Date.now() >= deadline) {
                finish(isHostAlpineReady(window.Alpine) ? window.Alpine : null);

                return;
            }

            requestAnimationFrame(tick);
        };

        tick();
    });
}

/**
 * @returns {Promise<{ alpine: import('alpinejs').Alpine, shouldStart: boolean } | null>}
 */
async function resolveWidgetAlpine() {
    await waitForDocumentReady();

    const hostAlpine = await waitForHostAlpine();

    if (hostAlpine) {
        return { alpine: hostAlpine, shouldStart: false };
    }

    if (shouldDeferToHostAlpine()) {
        return null;
    }

    if (isHostAlpineReady(window.Alpine)) {
        return { alpine: window.Alpine, shouldStart: false };
    }

    return { alpine: Alpine, shouldStart: true };
}

function cleanupWidgetThemeSubscriptions() {
    if (typeof window.__chatbotThemeUnsubscribe === 'function') {
        window.__chatbotThemeUnsubscribe();
        window.__chatbotThemeUnsubscribe = null;
    }
}

function removeExistingWidgetHosts() {
    cleanupWidgetThemeSubscriptions();

    document.querySelectorAll('[id^="chatbot-widget-host-"]').forEach((element) => {
        element.remove();
    });
}

/**
 * @param {HTMLScriptElement | null | undefined} scriptElement
 * @param {{ botKey?: string, apiBase?: string, scriptUrl?: string }} [options]
 */
export async function initWidget(scriptElement = null, options = {}) {
    const script = resolveScript(scriptElement, options);
    const botKey = options.botKey ?? script?.getAttribute('data-bot-key');

    if (! botKey) {
        console.error('[chatbot] data-bot-key attribútum hiányzik a script tagről.');

        return;
    }

    const apiBase = resolveApiBase(script);
    const api = createApiClient(apiBase, botKey);

    let config;

    try {
        config = await api.fetchConfig();
    } catch (error) {
        console.error('[chatbot] Konfiguráció betöltése sikertelen:', error);

        return;
    }

    if (! config.is_active) {
        // Korábban néma return volt — a fejlesztő konzolban sem látszott, miért nincs launcher.
        console.warn('[chatbot] A chatbot inaktív — a widget nem jelenik meg.');

        return;
    }

    const resolved = await resolveWidgetAlpine();

    if (resolved === null) {
        console.warn(
            '[chatbot] Filament admin Alpine nem állt készen — widget inicializálás kihagyva.',
        );

        return;
    }

    const { alpine, shouldStart } = resolved;

    removeExistingWidgetHosts();

    const shadow = mountShadowHost(botKey);

    const style = document.createElement('style');
    style.textContent = widgetCss;
    shadow.appendChild(style);

    const mount = document.createElement('div');
    shadow.appendChild(mount);

    registerChatWidget(alpine);

    const root = document.createElement('div');
    root.setAttribute(
        'x-data',
        `chatWidget(${JSON.stringify(botKey)}, ${JSON.stringify(apiBase)}, ${JSON.stringify(config)})`,
    );
    root.innerHTML = chatWidgetTemplate().trim();
    mount.appendChild(root);

    alpine.initTree(mount);

    // Saját Alpine.start() csak ügyfél oldalon — ne írja felül a host window.Alpine-ot.
    if (shouldStart && ! window.__chatbotAlpineStarted) {
        const hostAlpineBeforeStart = isHostAlpineReady(window.Alpine)
            ? window.Alpine
            : null;

        alpine.start();

        if (hostAlpineBeforeStart) {
            window.Alpine = hostAlpineBeforeStart;
        }

        window.__chatbotAlpineStarted = true;
    }
}
